/* Publieke tenant-specifieke aanmeldpagina.
   Route: /aanmelden/:slug — multistep formulier voor ouders zonder login.
   Reuse van bestaande public endpoints via /api/public/tenant/:slug/... */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Baby, CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: false,
});

const DAGDELEN = [
  { value: "hele_dag", label: "Hele dag (10,5u)" },
  { value: "ochtend", label: "Ochtend (5u)" },
  { value: "middag", label: "Middag (5u)" },
  { value: "voorschools", label: "Voorschools (2u)" },
  { value: "naschools", label: "Naschools BSO (3,5u)" },
];
const WEEKDAYS = ["Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag"];

export default function PublicAanmeldPage() {
  const { slug } = useParams();
  const [tenant, setTenant] = useState(null);
  const [slots, setSlots] = useState([]);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    kind_voornaam: "", kind_achternaam: "",
    kind_geboorte_status: "geboren", kind_geboortedatum: "", kind_uitgerekende_datum: "",
    ouder_voornaam: "", ouder_achternaam: "", ouder_email: "", ouder_telefoon: "",
    ouder_adres: "", ouder_postcode: "", ouder_stad: "",
    partner_voornaam: "", partner_achternaam: "", partner_email: "", partner_telefoon: "",
    location_id: "", opvangsoort: "",
    gewenste_startdatum: "",
    gewenste_dagen: [], // {weekday, dagdeel}
    opmerkingen: "",
    // Rondleiding
    rondleiding_wens: "geen",  // "geen" | "slot" | "voorkeur"
    rondleiding_slot_id: "",
    rondleiding_voorkeur_datum: "",
    rondleiding_voorkeur_tijd: "",
    rondleiding_opmerking: "",
    accept_privacy: false,
    accept_algemene_voorwaarden: false,
    website: "", // honeypot
  });

  useEffect(() => {
    api.get(`/public/tenant/${slug}`)
      .then(r => {
        setTenant(r.data);
        // Zet CSS var voor tenant kleur
        if (r.data.kleur_primair) {
          document.documentElement.style.setProperty("--tenant-primair", r.data.kleur_primair);
        }
      })
      .catch(e => setError(formatApiError(e.response?.data?.detail) || "Organisatie niet gevonden"));
  }, [slug]);

  // Fetch beschikbare rondleiding-slots voor gekozen locatie
  const fetchSlots = async (locId) => {
    if (!locId) { setSlots([]); return; }
    try {
      const { data } = await api.get(`/public/tenant/${slug}/rondleiding-slots?location_id=${locId}`);
      setSlots(data.slots || []);
    } catch { setSlots([]); }
  };
  useEffect(() => { fetchSlots(form.location_id); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug, form.location_id]);

  const primair = tenant?.kleur_primair || "#2D4A3E";
  const locName = useMemo(
    () => tenant?.locations?.find(l => l.id === form.location_id)?.name || "",
    [tenant, form.location_id]
  );

  const toggleDag = (weekday, dagdeel) => {
    const idx = form.gewenste_dagen.findIndex(d => d.weekday === weekday);
    if (idx >= 0) {
      const copy = [...form.gewenste_dagen];
      if (copy[idx].dagdeel === dagdeel) copy.splice(idx, 1);
      else copy[idx] = { weekday, dagdeel };
      setForm({ ...form, gewenste_dagen: copy });
    } else {
      setForm({ ...form, gewenste_dagen: [...form.gewenste_dagen, { weekday, dagdeel }] });
    }
  };

  const canProceed = () => {
    if (step === 1) {
      if (!form.kind_voornaam || !form.kind_achternaam) return false;
      if (form.kind_geboorte_status === "ongeboren") return !!form.kind_uitgerekende_datum;
      return !!form.kind_geboortedatum;
    }
    if (step === 2) return form.ouder_voornaam && form.ouder_achternaam && form.ouder_email && form.ouder_telefoon;
    if (step === 3) return form.location_id && form.gewenste_startdatum && form.gewenste_dagen.length > 0;
    if (step === 4) return form.accept_privacy && form.accept_algemene_voorwaarden;
    return true;
  };

  const submit = async () => {
    if (!canProceed()) { toast.error("Vul verplichte velden in"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      // Verwijder lege optionele velden
      if (!payload.partner_email) { delete payload.partner_email; }
      // Rondleiding: geen submenu → geen rondleiding
      let rondleiding = null;
      if (form.rondleiding_wens === "slot" && form.rondleiding_slot_id) {
        rondleiding = { slot_id: form.rondleiding_slot_id, opmerking: form.rondleiding_opmerking || null };
      } else if (form.rondleiding_wens === "voorkeur" && form.rondleiding_voorkeur_datum) {
        rondleiding = {
          voorkeur_datum: form.rondleiding_voorkeur_datum,
          voorkeur_tijd: form.rondleiding_voorkeur_tijd || null,
          opmerking: form.rondleiding_opmerking || null,
        };
      }
      // Strip UI-only fields
      ["rondleiding_wens", "rondleiding_slot_id", "rondleiding_voorkeur_datum",
       "rondleiding_voorkeur_tijd", "rondleiding_opmerking"].forEach(k => delete payload[k]);
      if (rondleiding) payload.rondleiding = rondleiding;
      const { data } = await api.post(`/public/tenant/${slug}/aanmelden`, payload);
      setSubmitted(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Aanmelden mislukt — probeer opnieuw");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-neutral-50">
        <Card className="max-w-md p-8 text-center bg-white">
          <div className="text-xl font-display mb-2">Niet gevonden</div>
          <div className="text-sm text-neutral-600">{error}</div>
        </Card>
      </div>
    );
  }

  if (!tenant) {
    return <div className="min-h-screen flex items-center justify-center text-neutral-500">Laden…</div>;
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: `${primair}0d` }}>
        <Card className="max-w-lg p-10 bg-white text-center" data-testid="aanmeld-success">
          <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: primair }}>
            <CheckCircle2 className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-display mb-2">Aanmelding ontvangen!</h1>
          <p className="text-sm text-neutral-600 mb-4">
            Bedankt voor uw aanmelding bij <strong>{tenant.name}</strong>.
            We hebben uw aanvraag voor <strong>{form.kind_voornaam} {form.kind_achternaam}</strong> ontvangen.
          </p>
          <div className="my-4 p-4 rounded-md bg-neutral-50 text-xs">
            <div className="uppercase tracking-widest text-neutral-500 mb-1">Uw referentie</div>
            <div className="font-mono text-lg tracking-wider">{submitted.referentie}</div>
          </div>
          {submitted.status_url && (
            <a href={submitted.status_url} className="block mt-4" data-testid="status-url-link">
              <Button className="text-white w-full" style={{ background: primair }}>
                Volg status van uw aanmelding
              </Button>
            </a>
          )}
          <p className="text-xs text-neutral-500 mt-4">
            U ontvangt binnen 5 werkdagen bericht met ofwel een concreet aanbod, of een plek op onze wachtlijst.
            De statuslink is ook naar u gemaild.
          </p>
        </Card>
      </div>
    );
  }

  const stepPct = (step / 4) * 100;

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(135deg, ${primair}12, ${primair}05)` }}>
      {/* Header */}
      <div className="max-w-3xl mx-auto p-6">
        <div className="flex items-center gap-4 mb-8">
          {tenant.logo_url ? (
            <img src={tenant.logo_url.startsWith("http") ? tenant.logo_url : `${process.env.REACT_APP_BACKEND_URL}${tenant.logo_url}`}
                 alt="logo" className="w-14 h-14 rounded-xl object-cover" />
          ) : (
            <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: primair }}>
              <Baby className="h-7 w-7 text-white" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-display leading-tight" data-testid="tenant-name">{tenant.name}</h1>
            <div className="text-xs text-neutral-500">Aanmeldformulier · {tenant.email || ""}</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
            <span>Kind</span><span>Ouder(s)</span><span>Opvang</span><span>Bevestiging</span>
          </div>
          <div className="h-1.5 bg-neutral-200 rounded-full overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${stepPct}%`, background: primair }} />
          </div>
        </div>

        <Card className="p-6 bg-white" data-testid="aanmeld-form-card">
          {/* Honeypot */}
          <input type="text" name="website" value={form.website} onChange={e => setForm({ ...form, website: e.target.value })}
                  className="absolute -left-[9999px]" tabIndex={-1} autoComplete="off" aria-hidden />

          {/* STAP 1: Kind */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-display mb-1">Over uw kind</h2>
                <p className="text-xs text-neutral-500">Vul de gegevens van het kind in dat u wilt aanmelden.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Voornaam *</Label>
                  <Input value={form.kind_voornaam} onChange={e => setForm({ ...form, kind_voornaam: e.target.value })} data-testid="kind-voornaam" />
                </div>
                <div><Label>Achternaam *</Label>
                  <Input value={form.kind_achternaam} onChange={e => setForm({ ...form, kind_achternaam: e.target.value })} data-testid="kind-achternaam" />
                </div>
                <div className="col-span-2">
                  <Label>Is uw kind al geboren? *</Label>
                  <div className="flex gap-2 mt-1" data-testid="kind-geboorte-status">
                    {[
                      ["geboren", "Ja, al geboren"],
                      ["ongeboren", "Nog niet — ik heb een uitgerekende datum"],
                    ].map(([v, l]) => (
                      <button key={v} type="button"
                        onClick={() => setForm({
                          ...form,
                          kind_geboorte_status: v,
                          kind_geboortedatum: v === "geboren" ? form.kind_geboortedatum : "",
                          kind_uitgerekende_datum: v === "ongeboren" ? form.kind_uitgerekende_datum : "",
                        })}
                        className={`flex-1 text-sm px-3 py-2 rounded-md border transition-colors text-left ${
                          form.kind_geboorte_status === v
                            ? "bg-[color:var(--sage)] text-white border-[color:var(--sage)]"
                            : "border-neutral-200 hover:bg-neutral-100"
                        }`}
                        data-testid={`kind-status-${v}`}
                      >
                        {form.kind_geboorte_status === v && <span className="mr-1">✓</span>}{l}
                      </button>
                    ))}
                  </div>
                </div>
                {form.kind_geboorte_status === "ongeboren" ? (
                  <div className="col-span-2"><Label>Uitgerekende datum *</Label>
                    <Input type="date"
                           value={form.kind_uitgerekende_datum || ""}
                           onChange={e => setForm({ ...form, kind_uitgerekende_datum: e.target.value })}
                           data-testid="kind-uitgerekende-datum" />
                    <p className="text-xs text-neutral-500 mt-1">
                      De KDV kan alvast een voorlopige plek reserveren op basis van de uitgerekende datum.
                    </p>
                  </div>
                ) : (
                  <div className="col-span-2"><Label>Geboortedatum *</Label>
                    <Input type="date"
                           value={form.kind_geboortedatum}
                           onChange={e => setForm({ ...form, kind_geboortedatum: e.target.value })}
                           data-testid="kind-geboortedatum" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STAP 2: Ouder(s) */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-display mb-1">Uw gegevens</h2>
                <p className="text-xs text-neutral-500">Wij nemen contact op via deze e-mail en telefoon.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Voornaam *</Label>
                  <Input value={form.ouder_voornaam} onChange={e => setForm({ ...form, ouder_voornaam: e.target.value })} data-testid="ouder-voornaam" />
                </div>
                <div><Label>Achternaam *</Label>
                  <Input value={form.ouder_achternaam} onChange={e => setForm({ ...form, ouder_achternaam: e.target.value })} data-testid="ouder-achternaam" />
                </div>
                <div><Label>E-mail *</Label>
                  <Input type="email" value={form.ouder_email} onChange={e => setForm({ ...form, ouder_email: e.target.value })} data-testid="ouder-email" />
                </div>
                <div><Label>Telefoon *</Label>
                  <Input value={form.ouder_telefoon} onChange={e => setForm({ ...form, ouder_telefoon: e.target.value })} data-testid="ouder-telefoon" />
                </div>
                <div className="col-span-2"><Label>Adres</Label>
                  <Input value={form.ouder_adres} onChange={e => setForm({ ...form, ouder_adres: e.target.value })} />
                </div>
                <div><Label>Postcode</Label>
                  <Input value={form.ouder_postcode} onChange={e => setForm({ ...form, ouder_postcode: e.target.value })} />
                </div>
                <div><Label>Woonplaats</Label>
                  <Input value={form.ouder_stad} onChange={e => setForm({ ...form, ouder_stad: e.target.value })} />
                </div>
              </div>
              <details className="text-xs pt-2">
                <summary className="cursor-pointer text-neutral-600 hover:text-neutral-900">+ Tweede ouder/verzorger toevoegen (optioneel)</summary>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div><Label>Voornaam</Label>
                    <Input value={form.partner_voornaam} onChange={e => setForm({ ...form, partner_voornaam: e.target.value })} />
                  </div>
                  <div><Label>Achternaam</Label>
                    <Input value={form.partner_achternaam} onChange={e => setForm({ ...form, partner_achternaam: e.target.value })} />
                  </div>
                  <div><Label>E-mail</Label>
                    <Input type="email" value={form.partner_email} onChange={e => setForm({ ...form, partner_email: e.target.value })} />
                  </div>
                  <div><Label>Telefoon</Label>
                    <Input value={form.partner_telefoon} onChange={e => setForm({ ...form, partner_telefoon: e.target.value })} />
                  </div>
                </div>
              </details>
            </div>
          )}

          {/* STAP 3: Opvang */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-display mb-1">Gewenste opvang</h2>
                <p className="text-xs text-neutral-500">Kies locatie, startdatum en dagen. Uw voorkeur — wij checken de beschikbaarheid.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Locatie *</Label>
                  <Select value={form.location_id} onValueChange={v => {
                    setForm({ ...form, location_id: v, rondleiding_slot_id: "" });
                    fetchSlots(v);
                  }}>
                    <SelectTrigger data-testid="locatie-select"><SelectValue placeholder="Kies een locatie" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {tenant.locations.map(l => (
                        <SelectItem key={l.id} value={l.id}>{l.name} · {l.city || ""} · {l.type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Type opvang</Label>
                  <Select value={form.opvangsoort} onValueChange={v => setForm({ ...form, opvangsoort: v })}>
                    <SelectTrigger><SelectValue placeholder="KDV/BSO/PSZ" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {(tenant.opvangsoorten || ["KDV", "BSO"]).map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Gewenste startdatum *</Label>
                  <Input type="date" value={form.gewenste_startdatum} onChange={e => setForm({ ...form, gewenste_startdatum: e.target.value })} data-testid="startdatum" />
                </div>
              </div>

              <div>
                <Label>Dagen & dagdelen *</Label>
                <div className="mt-2 space-y-2">
                  {WEEKDAYS.map((lbl, i) => {
                    const cur = form.gewenste_dagen.find(d => d.weekday === i);
                    return (
                      <div key={i} className="flex items-center gap-2 flex-wrap p-2 rounded border border-neutral-200">
                        <div className="w-24 text-sm font-medium">{lbl}</div>
                        {DAGDELEN.map(dd => (
                          <button key={dd.value} type="button"
                            onClick={() => toggleDag(i, dd.value)}
                            data-testid={`dag-${i}-${dd.value}`}
                            className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                              cur?.dagdeel === dd.value
                                ? "text-white border-transparent"
                                : "border-neutral-300 text-neutral-600 hover:border-neutral-500"
                            }`}
                            style={cur?.dagdeel === dd.value ? { background: primair } : {}}>
                            {dd.label}
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label>Opmerkingen (optioneel)</Label>
                <Textarea rows={3} value={form.opmerkingen} onChange={e => setForm({ ...form, opmerkingen: e.target.value })}
                  placeholder="Allergieën, medische informatie, broertje/zusje op de opvang, etc." />
              </div>

              {/* Rondleiding — optioneel */}
              <div className="border-t border-neutral-200 pt-4">
                <Label className="flex items-center gap-1.5"><span>Rondleiding plannen (optioneel)</span></Label>
                <p className="text-xs text-neutral-500 mb-2">Kom vrijblijvend langs voor een rondleiding voordat u een definitieve keuze maakt.</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {[
                    { v: "geen", l: "Nee, bedankt" },
                    { v: "slot", l: `Uit beschikbare momenten (${slots.length})`, disabled: slots.length === 0 },
                    { v: "voorkeur", l: "Eigen voorkeur invullen" },
                  ].map(o => (
                    <button key={o.v} type="button" disabled={o.disabled}
                      onClick={() => setForm({ ...form, rondleiding_wens: o.v })}
                      data-testid={`rondleiding-${o.v}`}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                        form.rondleiding_wens === o.v ? "text-white border-transparent" :
                        o.disabled ? "border-neutral-200 text-neutral-300 cursor-not-allowed" : "border-neutral-300 text-neutral-600 hover:border-neutral-500"
                      }`}
                      style={form.rondleiding_wens === o.v ? { background: primair } : {}}>
                      {o.l}
                    </button>
                  ))}
                </div>
                {form.rondleiding_wens === "slot" && slots.length > 0 && (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {slots.map(s => (
                      <button type="button" key={s.id}
                        onClick={() => setForm({ ...form, rondleiding_slot_id: s.id })}
                        data-testid={`slot-${s.id}`}
                        className={`w-full text-left p-2 rounded border text-xs transition-colors ${
                          form.rondleiding_slot_id === s.id
                            ? "border-transparent text-white"
                            : "border-neutral-200 hover:border-neutral-400 bg-white"
                        }`}
                        style={form.rondleiding_slot_id === s.id ? { background: primair } : {}}>
                        <span className="font-medium">{new Date(s.datum).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                        {" · "}{s.tijd_van}–{s.tijd_tot}
                        <span className="opacity-70 ml-2">({s.capaciteit - (s.geboekt || 0)} plek(ken) vrij)</span>
                      </button>
                    ))}
                  </div>
                )}
                {form.rondleiding_wens === "voorkeur" && (
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Voorkeur-datum</Label>
                      <Input type="date" value={form.rondleiding_voorkeur_datum} onChange={e => setForm({ ...form, rondleiding_voorkeur_datum: e.target.value })} data-testid="rondleiding-datum" />
                    </div>
                    <div><Label className="text-xs">Voorkeur-tijd</Label>
                      <Input type="time" value={form.rondleiding_voorkeur_tijd} onChange={e => setForm({ ...form, rondleiding_voorkeur_tijd: e.target.value })} />
                    </div>
                  </div>
                )}
                {form.rondleiding_wens !== "geen" && (
                  <div className="mt-2">
                    <Label className="text-xs">Opmerking (optioneel)</Label>
                    <Input value={form.rondleiding_opmerking} onChange={e => setForm({ ...form, rondleiding_opmerking: e.target.value })}
                      placeholder="Bijv. Komt met partner" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STAP 4: Bevestiging */}
          {step === 4 && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-display mb-1">Bevestigen & akkoord</h2>
                <p className="text-xs text-neutral-500">Controleer uw gegevens en accepteer de voorwaarden.</p>
              </div>
              <div className="p-4 rounded-md bg-neutral-50 text-sm space-y-1">
                <div><span className="text-neutral-500">Kind:</span> <strong>{form.kind_voornaam} {form.kind_achternaam}</strong> · {
                  form.kind_geboorte_status === "ongeboren"
                    ? <span>uitgerekende datum {form.kind_uitgerekende_datum}</span>
                    : <span>geb. {form.kind_geboortedatum}</span>
                }</div>
                <div><span className="text-neutral-500">Ouder:</span> {form.ouder_voornaam} {form.ouder_achternaam} · {form.ouder_email}</div>
                <div><span className="text-neutral-500">Locatie:</span> {locName}</div>
                <div><span className="text-neutral-500">Startdatum:</span> {form.gewenste_startdatum}</div>
                <div><span className="text-neutral-500">Dagen:</span> {form.gewenste_dagen.length === 0
                  ? "—" : form.gewenste_dagen.map(d => `${WEEKDAYS[d.weekday]}(${d.dagdeel})`).join(", ")}</div>
              </div>
              <div className="space-y-2">
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="accept-privacy-row">
                  <Checkbox checked={form.accept_privacy} onCheckedChange={v => setForm({ ...form, accept_privacy: !!v })} data-testid="accept-privacy" />
                  <span>Ik ga akkoord met de <a href="#" className="underline">privacyverklaring</a> — mijn gegevens worden alleen gebruikt voor de aanmeldprocedure. *</span>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="accept-av-row">
                  <Checkbox checked={form.accept_algemene_voorwaarden} onCheckedChange={v => setForm({ ...form, accept_algemene_voorwaarden: !!v })} data-testid="accept-av" />
                  <span>Ik ga akkoord met de <a href="#" className="underline">algemene voorwaarden</a> van {tenant.name}. *</span>
                </label>
              </div>
              <div className="text-xs text-neutral-500 flex items-start gap-2 mt-4">
                <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" style={{ color: primair }} />
                <span>Uw gegevens worden veilig verwerkt volgens de AVG. U ontvangt een bevestigingsmail op <strong>{form.ouder_email}</strong>.</span>
              </div>
            </div>
          )}

          {/* Navigatie */}
          <div className="flex justify-between items-center mt-8 pt-4 border-t border-neutral-200">
            <Button variant="ghost" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} data-testid="prev-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Vorige
            </Button>
            {step < 4 ? (
              <Button onClick={() => canProceed() ? setStep(step + 1) : toast.error("Vul verplichte velden in")}
                style={{ background: primair }} className="text-white" data-testid="next-btn">
                Volgende <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={busy || !canProceed()}
                style={{ background: primair }} className="text-white" data-testid="submit-btn">
                {busy ? "Bezig…" : <><Sparkles className="h-4 w-4 mr-1" /> Verstuur aanmelding</>}
              </Button>
            )}
          </div>
        </Card>

        <div className="text-center text-[10px] text-neutral-400 mt-6">
          Aanmelding via {tenant.name} · Powered by <strong>Webster</strong>
        </div>
      </div>
    </div>
  );
}
